import { distanceM, MAX_SAMPLE_ACCURACY_M, nearestStore, shouldSample, storeLocation, trustedLocation, type Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';
import { getDeviceId } from '../db/clock';
import { commit } from '../db/commit';
import { db } from '../db/db';
import type { VisitRow } from '../db/types';
import { geoActive, getPosition, usePosition, type GeoPosition } from '../lib/geo';
import { buildMergeMap, resolveWith } from './products';

/** Próbka dalej niż to od pewnego położenia sklepu to pomyłka (np. odhaczanie w domu na liście z innym sklepem). */
const SAMPLE_MAX_FROM_TRUSTED_M = 3000;

interface Located {
  storeId: Id;
  loc: { lat: number; lng: number };
}

/**
 * Położenie każdego sklepu (bez scalonych duplikatów): z próbek wizyt, a bez nich z adresu.
 * Wizyty scalonych duplikatów liczą się sklepowi, który został.
 */
async function loadLocations(): Promise<Located[]> {
  const [stores, visits] = await Promise.all([db.stores.toArray(), db.visits.toArray()]);
  const resolve = resolveWith(buildMergeMap(stores));
  const samples = new Map<Id, VisitRow[]>();
  for (const v of visits) {
    if (v.deleted || v.lat == null || v.lng == null) continue;
    const id = resolve(v.storeId);
    const list = samples.get(id);
    if (list) list.push(v);
    else samples.set(id, [v]);
  }
  const out: Located[] = [];
  for (const s of stores) {
    if (s.deleted || s.mergedInto) continue;
    const loc = storeLocation(s, samples.get(s.id) ?? []);
    if (loc) out.push({ storeId: s.id, loc: { lat: loc.lat, lng: loc.lng } });
  }
  return out;
}

export interface NearbyStore {
  /** null = pozycja nieznana albo lokalizacja wyłączona. */
  position: GeoPosition | null;
  /** Najbliższy sklep w zasięgu (próg zależy od dokładności pozycji). */
  nearest: { storeId: Id; distanceM: number } | null;
  /** Odległość do każdego sklepu, którego położenie znamy. */
  distances: Map<Id, number>;
}

/** Sklep, w którym (prawdopodobnie) jestem, i odległości do sklepów — do podpowiedzi. */
export function useNearbyStore(): NearbyStore {
  const position = usePosition();
  const active = position !== null;
  const located = useLiveQuery(async () => (active ? loadLocations() : []), [active]);
  return useMemo(() => {
    const distances = new Map<Id, number>();
    if (!position || !located) return { position, nearest: null, distances };
    for (const l of located) distances.set(l.storeId, distanceM(position, l.loc));
    const hit = nearestStore(position, located.map((l) => ({ store: { id: l.storeId }, loc: l.loc })));
    return { position, nearest: hit ? { storeId: hit.store.id, distanceM: hit.distanceM } : null, distances };
  }, [position, located]);
}

/** Wizyty, dla których trwa odczyt — przy szybkim odhaczaniu jeden naraz. */
const sampling = new Set<Id>();

/**
 * Próbka położenia sklepu z wizyty. Wołane po każdym odhaczeniu, dopóki wizyta nie ma próbki;
 * core (shouldSample) przepuszcza tylko prawdziwe zakupy (odhaczanie hurtem w domu nie daje próbki),
 * a nieudany odczyt ponawia się przy kolejnych odhaczeniach. Działa PO zatwierdzeniu odhaczenia,
 * bo czekanie na GPS zamknęłoby transakcję Dexie. Nigdy nie pyta o zgodę.
 */
export async function maybeSampleVisit(visitId: Id): Promise<void> {
  // Zajęte przed pierwszym await — inaczej dwa szybkie odhaczenia przeszłyby to sprawdzenie razem.
  if (sampling.has(visitId)) return;
  sampling.add(visitId);
  try {
    if (!(await geoActive())) return;
    const visit = await db.visits.get(visitId);
    if (!visit || visit.deleted || visit.lat != null) return;
    const me = getDeviceId();
    const events = await db.orderEvents.where('storeId').equals(visit.storeId).toArray();
    const undone = new Set(events.filter((e) => e.kind === 'undo' && e.visitId === visitId).map((e) => e.targetId as Id));
    const checks = events
      .filter((e) => e.kind === 'item_checked' && e.visitId === visitId && e.deviceId === me && !undone.has(e.id))
      .sort((a, b) => (a.seq as number) - (b.seq as number))
      .map((e) => ({ wallMs: e.wallMs as number }));
    if (!shouldSample(checks)) return;

    const pos = await getPosition({ highAccuracy: true, timeoutMs: 10_000 });
    if (!pos || pos.accM > MAX_SAMPLE_ACCURACY_M) return;
    // Pewne położenie = próbki innych wizyt albo „Adres stąd”; zgeokodowany adres próbek nie blokuje.
    const [stores, visits] = await Promise.all([db.stores.toArray(), db.visits.toArray()]);
    const resolve = resolveWith(buildMergeMap(stores));
    const target = resolve(visit.storeId);
    const store = stores.find((s) => s.id === target);
    const others = visits.filter((v) => !v.deleted && v.id !== visitId && resolve(v.storeId) === target);
    const trusted = store ? trustedLocation(store, others) : null;
    if (trusted && distanceM(pos, trusted) > SAMPLE_MAX_FROM_TRUSTED_M) return;
    await commit(async (w) => {
      // Odczyt GPS trwa do 10 s — w tym czasie próbka mogła przyjść z synchronizacji.
      const current = await db.visits.get(visitId);
      if (!current || current.lat != null) return;
      await w.set('visits', visitId, { lat: pos.lat, lng: pos.lng, accM: Math.round(pos.accM) });
    });
  } finally {
    sampling.delete(visitId);
  }
}
